(ns assemble.resource.asset
  "Upload one asset, and upload queue for multiple assets."
  (:require [assemble.component.upload-modal :as upload-modal]
            [common.db.context :as context]
            [common.db.effects.promise]
            [re-frame.core :as rf]))

;;
;; Upload handlers
;;

(def entity-name "Asset Library")

(rf/reg-event-fx :assemble.resource.asset/success
  (fn [_ [_ asset-id queue-id]]
    {:fx [[:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message "Asset Uploaded"}]]
          [:dispatch-later {:ms 500 :dispatch [:assemble.resource.asset/get-updated-asset asset-id]}]
          [:dispatch [:assemble.resource.asset/upload-done queue-id]]]}))

(rf/reg-event-fx :assemble.resource.asset/failure
  (fn [_ [event queue-id error]]
    {:fx [[:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :message "Asset upload failed"
                       :event event
                       :response error}]]
          [:dispatch [:assemble.resource.asset/upload-error queue-id error]]]}))

(rf/reg-event-fx :assemble.resource.asset/get-updated-asset
  [(rf/inject-cofx :sub [:context/advertiser-id])]
  (fn [{:keys [context/advertiser-id]} [_ asset-id]]
    {:fx [[:cqrs/send
           {:type :asset/get-asset
            :data {:asset/id asset-id
                   :asset/advertiser-id advertiser-id}
            :then [:assemble.resource.asset/write-or-enqueue-update]}]]}))

(rf/reg-event-fx :assemble.resource.asset/write-or-enqueue-update
  (fn [{:keys [db]} [_ {:asset/keys [id upload-status asset-tree-id] :as asset}]]
    (let [max-retries 6
          try-count (get-in db [::update-try-count id] 1)
          wait-time (+ (js/Math.pow 5 try-count) 500 (rand-int (- 1000 500)))]
      ;; NOTE: This adds the new asset under :assets-by-node but not under :cqrs path in app-db.
      (if (and (< try-count max-retries) (nil? upload-status))
        {:fx [[:dispatch-later {:ms wait-time :dispatch [:assemble.resource.asset/get-updated-asset id]}]]
         :db (assoc-in db [::update-try-count id] (inc try-count))}
        {:db (-> (update-in db [::state :assets-by-node asset-tree-id] conj asset)
                 (update-in [::update-try-count] dissoc id))}))))

(rf/reg-event-fx :assemble.resource.asset/upload-flow
  [(rf/inject-cofx :sub [:context/advertiser-id])]
  (fn [{:keys [context/advertiser-id]} [_ current-node file metadata queue-id]]
    {:fx [[:cqrs/send
           {:type :asset/create-asset
            ;; E.g. name is required parameter, so error case
            ;; can be tested by commenting it out.
            :data {:asset/advertiser-id advertiser-id
                   :asset/asset-tree-id current-node
                   :asset/name (.-name file)
                   :asset/file-size (.-size file)
                   :asset/original-filename (.-name file)
                   :asset/metadata metadata}
            :then (fn [{:asset/keys [upload-url id]}]
                    (js/Promise. (fn [resolve reject]
                                   (let [req (js/XMLHttpRequest.)]
                                     (.addEventListener req "load"
                                                        (fn [e]
                                                          ;; e is ProgressEvent
                                                          ;; The response body is available in req.response
                                                          (if (<= 200 (.-status req) 299)
                                                            (do
                                                              (rf/dispatch [:assemble.resource.asset/success id queue-id])
                                                              (resolve e))
                                                            (reject e))))
                                     (.addEventListener (.-upload req) "progress"
                                                        (fn [e]
                                                          (when (.-lengthComputable e)
                                                            (let [pct (* 100 (/ (.-loaded e)
                                                                                (.-total e)))]
                                                              (rf/dispatch [:assemble.resource.asset/upload-progress queue-id pct])))))
                                     (.addEventListener req "error" (fn [e] (reject e)))
                                     ;; To test error case with file transfer, modify the url here.
                                     ;; Append something to the uri here -> 403 error -> load with error status.
                                     ;; Prepend something breaks uri scheme -> error event.
                                     (.open req "PUT" upload-url)
                                     ;; setRequestHeader must be after open
                                     (.setRequestHeader req "Content-Type" "application/octet-stream")
                                     (.send req file)))))
            :catch [:assemble.resource.asset/failure queue-id]}]]}))

;;
;; Upload queue
;;

(rf/reg-event-db :assemble.resource.asset/upload-progress
  (fn [db [_ queue-id progress]]
    (assoc-in db [:assets-upload :queue queue-id :progress] progress)))

(rf/reg-event-fx :assemble.resource.asset/upload-done
  (fn [{:keys [db]} [_ queue-id]]
    {:db (assoc-in db [:assets-upload :queue queue-id :status] "done")
     :fx [[:dispatch [:assemble.resource.asset/start-upload]]]}))

(rf/reg-event-fx :assemble.resource.asset/upload-error
  (fn [{:keys [db]} [_ queue-id error]]
    {:db (update-in db [:assets-upload :queue queue-id] assoc
                    :status "error"
                    ;; JS error message probably aren't useful to display.
                    :error error)
     :fx [[:dispatch [:assemble.resource.asset/start-upload]]]}))

;; If no upload is in progress, takes the first queued file, and starts uploading it.
;; Once upload is finished, or throws an error, the files status is updated and
;; the callback triggers this event again.
(rf/reg-event-fx :assemble.resource.asset/start-upload
  (fn [{:keys [db]} _]
    (let [files (:queue (:assets-upload db))
          active-item (upload-modal/queue-first-by-status files "uploading")
          first-item (upload-modal/queue-first-by-status files "queue")
          metadata (when (= :fonts (:asset-type (:assets-upload db)))
                     (merge (:metadata first-item)
                            {:font-family (:font-family (:assets-upload db))}))]
      ;; Modal is kept open when the uploads are done,
      ;; so the user sees all the status for the uploads.
      (when (and (not active-item) first-item)
        {:db (assoc-in db [:assets-upload :queue (:id first-item) :status] "uploading")
         :fx [[:dispatch [:assemble.resource.asset/upload-flow
                          (:folder-node (:assets-upload db))
                          (:file first-item)
                          metadata
                          (:id first-item)]]]}))))

;; Just change the status back from error to queue, and try
;; starting the upload again. If there is a uploading already
;; in progress, start-upload will be no-op, and this item will
;; be started after the previous upload finishes.
(rf/reg-event-fx :assemble.resource.asset/retry-upload
  (fn [{:keys [db]} [_ id]]
    {:db (update-in db [:assets-upload :queue id :status] (fn [status]
                                                            ;; Only allow error -> queue transition,
                                                            ;; the button should only be visible on this case, but
                                                            ;; to be extra careful to not break the state.
                                                            (if (= "error" status)
                                                              "queue"
                                                              status)))
     :fx [[:dispatch [:assemble.resource.asset/start-upload]]]}))

(rf/reg-event-db :assemble.resource.asset/set-metadata-field
  (fn [db [_ id field v]]
    (assoc-in db [:assets-upload :queue id :metadata field] v)))

(defn update-single-asset [assets {:asset/keys [id] :as asset}]
  (reduce (fn [acc {it-id :asset/id :as it}]
            (if (= it-id id)
              (conj acc asset)
              (conj acc it)))
          []
          assets))

(rf/reg-event-fx ::update-asset-success
  (fn [{:keys [db]} [_ resolve update-type {:asset/keys [asset-tree-id] :as asset}]]
    {:db (update-in db [::state :assets-by-node asset-tree-id] update-single-asset asset)
     :fx [[:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message (str "Asset has been " update-type "d")}]]
          [:promise/resolve resolve]]}))

(rf/reg-event-fx ::update-asset-failure
  (fn [_ [event reject update-type error]]
    {:fx [[:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :message (str "Asset " update-type " failed")
                       :event event
                       :response error}]]
          [:promise/reject reject]]}))

(rf/reg-event-fx ::update-folder-success
  (fn [_ [_ resolve update-type]]
    {:fx [[:dispatch [:assemble.resource.asset-tree/list-folders]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message (str "Folder has been " update-type "d")}]]
          [:promise/resolve resolve]]}))

(rf/reg-event-fx ::update-folder-failure
  (fn [_ [event reject update-type error]]
    {:fx [[:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :message (str "Folder " update-type " failed: " (:message error))
                       :event event
                       :response error}]]
          [:promise/reject reject]]}))

(rf/reg-event-fx ::update-asset
  (fn [{:keys [db]} [_ resolve reject payload update-type]]
    {:fx [[:cqrs/send
           {:type :asset/update-asset
            :data (assoc payload :asset/advertiser-id (context/advertiser-id db))
            :then [::update-asset-success resolve update-type]
            :catch [::update-asset-failure reject update-type]}]]}))

(rf/reg-event-fx ::rename-asset
  (fn [_ [_ resolve reject asset-id new-name]]
    {:fx [[:dispatch [::update-asset
                      resolve
                      reject
                      {:asset/id asset-id
                       :asset/name new-name}
                      "rename"]]]}))

(rf/reg-event-fx ::rename-folder
  (fn [{:keys [db]} [_ resolve reject asset-tree-id new-name]]
    {:fx [[:cqrs/send
           {:type :asset/rename-folder
            :data {:asset-tree/id asset-tree-id
                   :asset-tree/name new-name
                   :asset-tree/advertiser-id (context/advertiser-id db)}
            :then [::update-folder-success resolve "rename"]
            :catch [::update-folder-failure reject "rename"]}]]}))

(rf/reg-event-fx ::archive-asset
  (fn [_ [_ resolve reject asset-id archived]]
    {:fx [[:dispatch [::update-asset resolve reject {:asset/id asset-id
                                                     :asset/archived archived} (if archived "archive" "unarchive")]]]}))

(rf/reg-event-fx ::update-font-family-success
  [(rf/inject-cofx :sub :assemble.page.assets.file-tree/current-node-id)]
  (fn [cofx [_ resolve {:font-family/keys [display-name]}]]
    {:fx [[:dispatch [::list-font-families-for-advertiser]]
          [:dispatch [::list-assets-for-node (:assemble.page.assets.file-tree/current-node-id cofx)]]
          [:dispatch [:assemble.assets-page/set-current-font-family display-name]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message "Font family name has been updated"}]]
          [:promise/resolve resolve]]}))

;;
;; List assets
;;

(rf/reg-event-fx :assemble.resource.asset/list-assets-for-node
  [(rf/inject-cofx :sub [:context/advertiser-id])]
  (fn [{:keys [context/advertiser-id]} [_ node-id]]
    {:fx [[:dispatch [:cqrs/send
                      {:type :asset/list-assets
                       :data {:asset/advertiser-id advertiser-id
                              :asset/asset-tree-id node-id}
                       :on-success [:assemble.resource.asset/write-assets-for-node node-id]}]]]}))

(rf/reg-event-db :assemble.resource.asset/write-assets-for-node
  (fn [db [_ node-id _ {:keys [body]}]]
    (assoc-in db [::state :assets-by-node node-id] body)))

(rf/reg-event-fx ::list-font-families-for-advertiser
  [(rf/inject-cofx :sub [:context/advertiser-id])]
  (fn [{:keys [context/advertiser-id]} _]
    {:fx [[:dispatch [:cqrs/send
                      {:type :asset/list-font-families
                       :data {:font-family/advertiser-id advertiser-id}
                       :on-success [::write-font-families-for-advertiser advertiser-id]}]]]}))

(rf/reg-event-db ::write-font-families-for-advertiser
  (fn [db [_ advertiser-id _ {:keys [body]}]]
    (assoc-in db [::state :font-families-by-advertiser advertiser-id] body)))

(rf/reg-event-fx :assemble.resource.asset/update-font-family-name
  (fn [{:keys [db]} [_ resolve reject payload]]
    {:fx [[:cqrs/send
           {:type
            :asset/update-font-family-name
            :data (assoc payload :font-family/advertiser-id (context/advertiser-id db))
            :then [::update-font-family-success resolve]
            :catch [::update-asset-failure reject]}]]}))

(rf/reg-sub :assemble.resource.asset/state
  (fn [db]
    (::state db)))

(rf/reg-sub :assemble.resource.asset/assets-by-node
  :<- [:assemble.resource.asset/state]
  (fn [state [_ node-id]]
    (get-in state [:assets-by-node node-id])))

(rf/reg-sub :assemble.resource.asset/fonts-by-node-and-advertiser-id
  (fn [[_ node-id]]
    (rf/subscribe [:assemble.resource.asset/assets-by-node node-id]))
  (fn [assets _]
    (->> assets
         (group-by (comp :font-family :asset/metadata))
         ;; Grid will keep the order for groups, and sorting
         ;; will sort just the children.
         (sort-by key)
         (mapcat (fn [[font-family fonts]]
                   (let [font-family (or font-family
                                         ;; FIXME: Shouldn't be possible, but have bad data on testing
                                         "Unknown")]
                     (map (fn [font]
                            (assoc font :path (to-array [font-family (:asset/id font)])))
                          fonts)))))))

(rf/reg-sub :assemble.resource.asset/font-families
  (fn [[_ node-id]]
    (rf/subscribe [:assemble.resource.asset/assets-by-node node-id]))
  (fn [assets _]
    (->> assets
         (keep (comp :font-family :asset/metadata))
         (set)
         (sort))))

;;
;; samples
;;

(comment

  (require '[lemonpi.domain.interface.asset :as asset])

 ;; listing folders
  (rf/dispatch [:cqrs/send {:type :asset/list-folders
                            :data {:asset-tree/parent-id asset/root-asset-id
                                   :asset-tree/advertiser-id 1}
                            :then (fn [result] (js/console.log result))}])

;; deleting folders
  (rf/dispatch
   [:cqrs/send {:type :asset/delete-folder
                :data {:asset-tree/id 5
                       :asset-tree/advertiser-id 2}
                :then {:type :asset/list-folders
                       :data {:asset-tree/parent-id asset/root-asset-id
                              :asset-tree/advertiser-id 2}}}])

 ;; creating folder
  (rf/dispatch [:cqrs/send {:type :asset/create-folder
                            :data {:asset-tree/name "nested2"
                                   :asset-tree/parent-id 5
                                   :asset-tree/advertiser-id 1}
                            :then {:type :asset/list-folders
                                   :data {:asset-tree/parent-id 1
                                          :asset-tree/advertiser-id 1
                                          :depth 10}}}])

 ;; creating folders
  (rf/dispatch
   [:cqrs/send {:type :asset/create-folders
                :data {:asset-tree/parent-id asset/root-asset-id
                       :asset-tree/advertiser-id 2
                       :data {:asset-tree/name "Assets"
                              :children [{:asset-tree/name "images"}
                                         {:asset-tree/name "videos"}
                                         {:asset-tree/name "audio"
                                          :children [{:asset-tree/name "music"}
                                                     {:asset-tree/name "effects"}]}
                                         {:asset-tree/name "Fonts"}]}}
                :then {:type :asset/list-folders
                       :data {:asset-tree/parent-id asset/root-asset-id
                              :asset-tree/advertiser-id 2}}}]))
